import { useState } from "react";
import { useTranslations } from "next-intl";
import { useParams } from "react-router-dom";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { smsChannelEnabledOf } from "@/lib/sms/smsCapabilities";
import { SentMailsList } from "./SentMailsList";
import { SentSmsList } from "./SentSmsList";

/**
 * The guest detail's "sent" tab. Without the SMS channel it IS the sent-mail
 * list, byte for byte as before. With the channel on (fail closed) and a role
 * that may read automations — the SMS history route is WORKFLOWS_READ — a
 * two-way switch "Emails | SMS" sits on top.
 *
 * One tab, not a new one: the rail's tab keys are part of the stored guest
 * detail layout and mirrored by main-backend (`GDL_SIDEBAR_TAB_KEYS`), so a
 * seventh key would need a backend change and every saved layout would read it
 * as unknown.
 */
export const SentMessagesPanel: React.FC<{ guestId: string }> = ({ guestId }) => {
  const t = useTranslations("smsGuest");
  // The host (GuestNotes) already reads the strict provider; same one here.
  const capabilities = useCapabilities();
  const { can } = capabilities;
  const smsChannelEnabled = smsChannelEnabledOf(capabilities);
  const params = useParams();
  const eventId = (params.eventId as string) ?? "";
  const [channel, setChannel] = useState<"mail" | "sms">("mail");
  const smsVisible = smsChannelEnabled && can("workflows:read");

  if (!smsVisible) return <SentMailsList guestId={guestId} />;

  const tab = (id: "mail" | "sms", label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={channel === id}
      data-testid={`sent-messages-tab-${id}`}
      onClick={() => setChannel(id)}
      className={`flex-1 rounded-lg px-3 py-1.5 body-3-medium ${
        channel === id ? "surface-dark font-light-primary" : "font-secondary"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex h-full flex-col">
      <div role="tablist" className="mt-3 flex flex-row gap-1 rounded-xl border outline-tertiary p-1 shrink-0">
        {tab("mail", t("tabMails"))}
        {tab("sms", t("tabSms"))}
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {channel === "mail" ? (
          <SentMailsList guestId={guestId} />
        ) : (
          <div className="flex h-full flex-col overflow-y-auto">
            <SentSmsList eventId={eventId} guestId={guestId} />
          </div>
        )}
      </div>
    </div>
  );
};
